import React, { useContext, useEffect } from 'react';
import { UseFormMethods } from 'react-hook-form';
import LoadingSpinner from 'src/components/common/LoadingSpinner';
import { FixedSizeList as List } from 'react-window';
import { LandSystem } from 'src/models/LandSystem';
import ReportDataTree from 'src/models/reports/ReportDataTree';
import ReportTownshipDto from 'src/models/reports/ReportTownshipDto';
import { AppContext } from 'src/providers/AppContextProvider';
import { IAppContextState } from 'src/reducers/AppContext';

interface IReportFilterLocationProps extends Partial<Pick<UseFormMethods, 'getValues' | 'setValue' | 'register'>> {
    // selectedHorizonIds: number[];
    townships: ReportTownshipDto[];
    landSystemId: LandSystem;
    selectAll: boolean;
    expandAll: boolean;
    loadingFields: boolean;
    selectedHorizons: ReportDataTree[];
    width: number;
    filteredList: ReportDataTree[];
    parentTier: number;

    refreshTagValues: () => void;
    handleSelect: (currentRow: ReportDataTree, isSelected: boolean, isTop: boolean) => void;
    setLoadingFields: (loading: boolean) => void;
    setSelectedHorizons: (horizons: ReportDataTree[]) => void;
    setFilteredList: (list: ReportDataTree[]) => void;
    setParentTier: (tier: number) => void;
    flattenItems: (nodes: ReportDataTree[], showChildren?: boolean, flattenedTree?: ReportDataTree[]) => ReportDataTree[];
}

const ReportFilterLocation = (props: IReportFilterLocationProps) => {
    const appContext = useContext<IAppContextState>(AppContext);
    const rowHeight = 20;
    
    useEffect(() => {
        if (props.townships?.length > 0) {
            props.setLoadingFields(false);
            props.refreshTagValues();
            let treeList: ReportDataTree[];
            let pTier: number;
            let global = `${appContext.clientNumber},${appContext.projectNumber}`;

            if (props.landSystemId === LandSystem.EastTexas) {
                // setExpandedFields([1]);
                global += `,${props.townships[0].fieldList[0].globalNumber}`;
                treeList = ReportDataTree.FromTracts(props.townships[0].fieldList[0].tractList, props.townships[0].fieldList[0].id, global);
                pTier = 3;
            } else if (props.landSystemId === LandSystem.RightOfWay) {
                treeList = ReportDataTree.FromFields(props.townships[0].fieldList, props.townships[0].id, global, 'ROW Line');
                pTier = 2;
            } else {
                let fieldType = props.landSystemId === LandSystem.Eastern ? 'Map' : 'Section';
                treeList = ReportDataTree.FromTownships(props.townships, global, fieldType);
                pTier = 1;
            }
            props.setParentTier(pTier);
            props.setFilteredList(props.flattenItems(treeList.filter(tl => tl.reportTier === pTier)));
        }
    }, [props.townships]);

    useEffect(() => {
        let newList = props.flattenItems(props.filteredList, true);
        for (let fl of newList) {
            fl.selected = props.selectAll;
            fl.expanded = fl.reportTier === props.parentTier && props.selectAll;
        }

        props.setFilteredList(props.flattenItems(newList.filter(fl => fl.reportTier === props.parentTier)));
    }, [props.selectAll]);

    useEffect(() => {
        let newList = props.flattenItems(props.filteredList, true);
        for (let fl of newList) {
            fl.expanded = props.expandAll;
        }

        props.setFilteredList(props.flattenItems(newList.filter(fl => fl.reportTier === props.parentTier)));
    }, [props.expandAll]);

    useEffect(() => {
        if (props.filteredList?.length > 0) {
            let filtered = props.filteredList.filter(fl => fl.selected);
            let hList = props.flattenItems(filtered, true).filter(fl => fl.reportTier === 4 && fl.selected);
            props.setSelectedHorizons(hList);
        }
    }, [props.filteredList]);

    const displayFlattenedList = () => {
        let flatRow = ({ index, style }: { index: number; style: React.CSSProperties }) => {
            const node = props.filteredList[index];
            let marginBuffer = '';

            switch (node.reportTier) {
                case 4:
                    marginBuffer = 'ml-6';
                    break;
                case 3:
                    marginBuffer = 'ml-4';
                    break;
                case 2:
                    marginBuffer = 'ml-2';
                    break;
                case 1:
                default:
                    break;
            }

            return (
                <div style={{ ...style, borderBottom: '1px solid grey' }}>
                    <div title={node.reportText} className={marginBuffer} style={{ display: 'flex', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <div className="clickable" onClick={() => handleExpand(node.id, node.reportTier)}>
                            +
                        </div>
                        <div>
                            <input
                                name={node.reportGlobal}
                                id={node.reportGlobal}
                                type="checkbox"
                                onChange={() => props.handleSelect(node, !node.selected, true)}
                                defaultChecked={node.selected}
                                ref={props.register}
                                disabled={props.loadingFields}
                            />
                        </div>

                        {node.reportText}
                    </div>
                </div>
            );
        };

        return (
            <List
                height={400}
                itemCount={props.filteredList.length}
                itemSize={rowHeight}
                width={props.width}
            >
                {flatRow}
            </List>
        );
    };
    
    const handleExpand = (nodeId: number, nodeTier: number) => {
        let flatList = [...props.filteredList];
        let flatRow = flatList.find((fl) => fl.id === nodeId && fl.reportTier === nodeTier);
        flatRow.expanded = !flatRow.expanded;
        
        let newFlat = props.flattenItems(flatList.filter(fl => fl.reportTier === props.parentTier));
        props.setFilteredList(newFlat);
    };

    return (
        <div>

            {props.loadingFields && (
                <>
                    <LoadingSpinner />
                    <div>Loading Fields</div>
                </>
            )}
            {props.filteredList?.length > 0 && (
                displayFlattenedList()
            )}
        </div>
    );
};

export default ReportFilterLocation;



// WEBPACK FOOTER //
// ./src/components/project/sections/ReportComponents/ReportFilterLocation.tsx